import React, { useEffect, useMemo, useState } from "react";
import { FaBuilding, FaLock, FaPlus, FaSearch, FaUsers } from "react-icons/fa";
import StaffLayout from "./StaffLayout";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import CustomerNameLink from "./CustomerNameLink";
import LinkFamilyMemberModal from "./LinkFamilyMemberModal";
import ManagerPinModal from "./ManagerPinModal";
import FamilyMemberAuditLogTab from "./FamilyMemberAuditLogTab";
import ProgramLifecycleActionsMenu from "./ProgramLifecycleActionsMenu";
import ProgramLifecycleActionModal from "./ProgramLifecycleActionModal";
import ProgramLifecycleBadge from "./ProgramLifecycleBadge";
import ProgramLifecycleCancelWizard from "./ProgramLifecycleCancelWizard";
import "./CorporateProgramsPage.css";

// CORP-METRICS-V1-EXPANSION (Sprint 18) — formatting helpers for the new
// metric tiles consuming `GET /api/organizations/:id/usage` summary
// additions. See `docs/backlog/CORP-METRICS-V1-EXPANSION-path-y-metrics-on-existing-endpoint.md`.

const COUNT_FORMATTER = new Intl.NumberFormat("en-US");

// Integer with thousands separator when > 999. Non-finite input → "0"
// (defensive against undefined / null / NaN — never surfaces "NaN").
const formatCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return COUNT_FORMATTER.format(Math.trunc(n));
};

// 0..1 float → percentage string with no decimal precision (e.g., 0.4 →
// "40%"). Non-finite input → "0%" (defensive: zero adoption renders as
// "0%", NOT "NaN%" — AC Scenario 6).
const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0%";
  return `${Math.round(n * 100)}%`;
};

// Dollars → "$X,XXX.XX" with thousands separator. BE returns
// corpRatePurchasedRevenue in DOLLARS (matches deployed
// `corporateRateAmount` convention; verified against PR #244 commit
// d6827b3 BE response shape — `3 × $25 → 75`). Non-finite input →
// "$0.00" (defensive). Uses Intl.NumberFormat for locale-aware comma
// grouping (e.g. 30000 → "$30,000.00").
const formatDollars = (dollars) => {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
};

// Session-type token → human label. Mirrors the SessionType labels used
// elsewhere in the app; unknown tokens render as the raw token (so a new
// BE-added session type surfaces visibly without an FE-side allowlist
// gate).
const SESSION_TYPE_LABELS = {
  infrared: "Infrared",
  infraredSauna: "Infrared sauna",
  redLight: "Red light",
  redlight: "Red light",
  halotherapy: "Halotherapy",
  aromatherapy: "Aromatherapy",
  massage: "Massage",
  suite: "Suite",
};

const formatSessionTypeLabel = (token) =>
  SESSION_TYPE_LABELS[token] || token;

const DEFAULT_PROGRAM_FORM = {
  name: "",
  sponsoredSessionsPerEmployeePerMonth: 4,
  monthlySponsoredCap: 310,
  corporateRate: 20,
  corporateRateStripePriceId: "",
  eligibleSessionTypes: ["infrared", "redlight"],
};

const DEFAULT_DOMAIN_RULE_FORM = {
  programId: "",
  domain: "",
  autoEnroll: true,
  requireEmailVerification: true,
  active: true,
  notes: "",
};

const CorporateProgramsPage = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [organizationForm, setOrganizationForm] = useState({
    name: "",
    code: "",
    notes: "",
  });
  const [programForm, setProgramForm] = useState(DEFAULT_PROGRAM_FORM);
  const [usageSnapshot, setUsageSnapshot] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [domainRules, setDomainRules] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeResults, setEmployeeResults] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [enrollmentForm, setEnrollmentForm] = useState({
    userId: "",
    employeeIdentifier: "",
  });
  const [domainRuleForm, setDomainRuleForm] = useState(
    DEFAULT_DOMAIN_RULE_FORM
  );

  // CORP-FAMILY-002 — page-level tab strip (Enrollments | Activity)
  const [activeTab, setActiveTab] = useState("enrollments");

  // CORP-FAMILY-002 — Link modal state
  const [linkModalEnrollment, setLinkModalEnrollment] = useState(null);
  const [linkInFlight, setLinkInFlight] = useState(false);
  const [linkError, setLinkError] = useState(null);

  // CORP-FAMILY-002 — Unlink (manager-PIN) modal state
  const [unlinkModalEnrollment, setUnlinkModalEnrollment] = useState(null);
  const [unlinkInFlight, setUnlinkInFlight] = useState(false);
  const [unlinkError, setUnlinkError] = useState(null);
  const [unlinkSuccessFlash, setUnlinkSuccessFlash] = useState(false);

  // CORP-FAMILY-003 — Cooldown-override (manager-PIN) modal state. The
  // override clears the 90-day anti-musical-chairs guard (ADR-037 D1) on a
  // specific enrollment so a new family member can be linked immediately.
  // REUSES the deployed ManagerPinModal component verbatim (PR #201 / ADR-035
  // uniform doctrine) — single-step PIN + REQUIRED reason. Failure modes
  // mirror the unlink modal: MANAGER_PIN_INVALID / MANAGER_PIN_NOT_PROVISIONED
  // / MANAGER_PIN_FORBIDDEN_ROLE surface inline; the auth interceptor
  // (PR #206 CORP-FAMILY-005) does NOT bounce to login on 403 manager-PIN
  // failures so the modal stays open for retry.
  const [cooldownOverrideModalEnrollment, setCooldownOverrideModalEnrollment] =
    useState(null);
  const [cooldownOverrideInFlight, setCooldownOverrideInFlight] = useState(false);
  const [cooldownOverrideError, setCooldownOverrideError] = useState(null);
  const [cooldownOverrideSuccessFlash, setCooldownOverrideSuccessFlash] =
    useState(false);

  // CORP-FAMILY-002 — track per-row in-flight to disable both buttons
  const [familyMemberRowInFlight, setFamilyMemberRowInFlight] = useState({});

  // CORP-PARTNERSHIP-SUSPEND-REACTIVATE-001 (Sprint 19, Story 1) —
  // lifecycle modal state. `variant` gates which single-step modal
  // renders (suspend vs reactivate). `inFlight` disables interaction
  // during the POST. `error` is a user-friendly string surfaced inline
  // in the modal so the operator can retry without closing.
  const [lifecycleModalVariant, setLifecycleModalVariant] = useState(null);
  const [lifecycleModalInFlight, setLifecycleModalInFlight] = useState(false);
  const [lifecycleModalError, setLifecycleModalError] = useState(null);

  // CORP-PARTNERSHIP-HARD-CANCEL-CORE-001 (Sprint 19, Story 2) — 4-step
  // cancel wizard state. The wizard is a separate component from the
  // single-step suspend/reactivate modal (spec §3 vs §2/§4) so we keep
  // its state siloed. `open` boolean drives visibility; `inFlight`
  // disables interaction during POST; `error` is surfaced inline in the
  // wizard so the operator can retry without losing wizard state.
  const [cancelWizardOpen, setCancelWizardOpen] = useState(false);
  const [cancelWizardInFlight, setCancelWizardInFlight] = useState(false);
  const [cancelWizardError, setCancelWizardError] = useState(null);

  const selectedOrganization = useMemo(
    () =>
      organizations.find((organization) => organization._id === selectedOrganizationId) ||
      null,
    [organizations, selectedOrganizationId]
  );

  const currentProgram = useMemo(() => {
    if (!usageSnapshot?.programs?.length) {
      return null;
    }

    return usageSnapshot.programs[0];
  }, [usageSnapshot]);

  const currentProgramUsage = useMemo(() => {
    if (!usageSnapshot?.usage?.length || !currentProgram?._id) {
      return null;
    }

    return (
      usageSnapshot.usage.find(
        (usageEntry) => usageEntry.program?._id === currentProgram._id
      ) || null
    );
  }, [currentProgram, usageSnapshot]);

  const employeeUsageRows = useMemo(() => {
    if (!enrollments.length) {
      return [];
    }

    return enrollments.map((enrollment) => {
      const employeeUsage =
        usageSnapshot?.employeeUsage?.find(
          (usageEntry) =>
            usageEntry.user?._id === enrollment.user?._id &&
            usageEntry.program?._id === enrollment.program?._id
        ) || null;

      const corporateRate =
        typeof employeeUsage?.program?.corporateRate === "number"
          ? employeeUsage.program.corporateRate
          : typeof currentProgram?.corporateRate === "number"
          ? currentProgram.corporateRate
          : 0;

      return {
        enrollment,
        employeeUsage,
        corporateRateAmount:
          (employeeUsage?.corporateRateUsed || 0) * Number(corporateRate || 0),
      };
    });
  }, [currentProgram, enrollments, usageSnapshot]);

  const exportMonthlyReport = () => {
    if (!selectedOrganization || !usageSnapshot) {
      return;
    }

    const lines = [
      [
        "Organization",
        "Year Month",
        "Employee",
        "Email",
        "Program",
        "Sponsored Used",
        "Corporate-Rate Used",
        "Employee-Paid Amount",
      ].join(","),
      ...employeeUsageRows.map(({ enrollment, employeeUsage, corporateRateAmount }) =>
        [
          `"${selectedOrganization.name}"`,
          usageSnapshot.yearMonth,
          `"${enrollment.user ? `${enrollment.user.firstName} ${enrollment.user.lastName}` : "Unknown member"}"`,
          `"${enrollment.user?.email || ""}"`,
          `"${enrollment.program?.name || currentProgram?.name || ""}"`,
          employeeUsage?.sponsoredUsed || 0,
          employeeUsage?.corporateRateUsed || 0,
          corporateRateAmount.toFixed(2),
        ].join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedOrganization.code || "organization"}-${
      usageSnapshot.yearMonth || "current-month"
    }-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const resetFlashMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadOrganizations = async (preferredOrganizationId = null) => {
    try {
      setLoading(true);
      const response = await staffAPI.getOrganizations();
      const orgs = response.data.organizations || [];
      setOrganizations(orgs);

      const nextSelectedOrganizationId =
        preferredOrganizationId ||
        selectedOrganizationId ||
        (orgs.length > 0 ? orgs[0]._id : "");

      setSelectedOrganizationId(nextSelectedOrganizationId);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.loadOrganizations",
        setError,
        "Unable to load corporate programs."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizationDetail = async (organizationId) => {
    if (!organizationId) {
      setUsageSnapshot(null);
      setEnrollments([]);
      setDomainRules([]);
      return;
    }

    try {
      setDetailLoading(true);
      const [usageResponse, enrollmentsResponse, domainRulesResponse] =
        await Promise.all([
        staffAPI.getOrganizationUsage(organizationId),
        staffAPI.getOrganizationEnrollments(organizationId),
        staffAPI.getOrganizationDomainRules(organizationId),
      ]);

      setUsageSnapshot(usageResponse.data);
      setEnrollments(enrollmentsResponse.data.enrollments || []);
      setDomainRules(domainRulesResponse.data.domainRules || []);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.loadOrganizationDetail",
        setError,
        "Unable to load organization details."
      );
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOrganizationDetail(selectedOrganizationId);
  }, [selectedOrganizationId]);

  useEffect(() => {
    setDomainRuleForm((current) => ({
      ...current,
      programId: currentProgram?._id || "",
    }));
  }, [currentProgram]);

  const handleOrganizationSubmit = async (event) => {
    event.preventDefault();
    resetFlashMessages();

    try {
      const response = await staffAPI.createOrganization(organizationForm);
      setSuccess("Organization created successfully.");
      setOrganizationForm({
        name: "",
        code: "",
        notes: "",
      });
      await loadOrganizations(response.data.organization._id);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.createOrganization",
        setError,
        "Unable to create the organization."
      );
    }
  };

  const handleProgramSubmit = async (event) => {
    event.preventDefault();
    resetFlashMessages();

    if (!selectedOrganizationId) {
      setError("Choose an organization before creating a program.");
      return;
    }

    try {
      await staffAPI.createOrganizationProgram(selectedOrganizationId, programForm);
      setSuccess("Program created successfully.");
      setProgramForm(DEFAULT_PROGRAM_FORM);
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.createProgram",
        setError,
        "Unable to create the program."
      );
    }
  };

  const handleEmployeeSearch = async () => {
    resetFlashMessages();

    if (!employeeSearch || employeeSearch.trim().length < 2) {
      setEmployeeResults([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const response = await staffAPI.searchCustomers({
        search: employeeSearch.trim(),
      });
      setEmployeeResults(response.data.customers || []);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.searchEmployees",
        setError,
        "Unable to search members."
      );
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  const handleEnrollmentSubmit = async (event) => {
    event.preventDefault();
    resetFlashMessages();

    if (!selectedOrganizationId || !currentProgram?._id) {
      setError("Create a program before enrolling employees.");
      return;
    }

    if (!enrollmentForm.userId) {
      setError("Select a member before saving the enrollment.");
      return;
    }

    try {
      await staffAPI.enrollOrganizationUser(
        selectedOrganizationId,
        currentProgram._id,
        enrollmentForm
      );
      setSuccess("Employee enrolled successfully.");
      setEnrollmentForm({ userId: "", employeeIdentifier: "" });
      setEmployeeSearch("");
      setEmployeeResults([]);
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.enrollUser",
        setError,
        "Unable to enroll the employee."
      );
    }
  };

  const handleDomainRuleSubmit = async (event) => {
    event.preventDefault();
    resetFlashMessages();

    if (!selectedOrganizationId) {
      setError("Choose an organization before creating a domain rule.");
      return;
    }

    if (!domainRuleForm.programId || !domainRuleForm.domain.trim()) {
      setError("Program and email domain are required.");
      return;
    }

    try {
      await staffAPI.createOrganizationDomainRule(
        selectedOrganizationId,
        domainRuleForm
      );
      setSuccess("Domain rule created successfully.");
      setDomainRuleForm({
        ...DEFAULT_DOMAIN_RULE_FORM,
        programId: currentProgram?._id || "",
      });
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.createDomainRule",
        setError,
        "Unable to create the domain rule."
      );
    }
  };

  const handleDomainRuleUpdate = async (domainRuleId, updates) => {
    resetFlashMessages();

    try {
      await staffAPI.updateOrganizationDomainRule(
        selectedOrganizationId,
        domainRuleId,
        updates
      );
      setSuccess("Domain rule updated.");
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      handleApiError(
        err,
        "CorporateProgramsPage.updateDomainRule",
        setError,
        "Unable to update the domain rule."
      );
    }
  };

  // CORP-PARTNERSHIP-SUSPEND-REACTIVATE-001 (Sprint 19, Story 1) —
  // lifecycle action handlers. Called from the ProgramLifecycleActionsMenu
  // wired next to the Active-program summary header. Consumes the API
  // methods added in services/api.js (POST /suspend, POST /reactivate).
  const openLifecycleModal = (variant) => {
    resetFlashMessages();
    setLifecycleModalError(null);
    setLifecycleModalVariant(variant);
  };

  const closeLifecycleModal = () => {
    if (lifecycleModalInFlight) return;
    setLifecycleModalVariant(null);
    setLifecycleModalError(null);
  };

  // CORP-PARTNERSHIP-HARD-CANCEL-CORE-001 (Sprint 19, Story 2) — 4-step
  // wizard open/close + submit handlers. Wizard body owns its own local
  // step + form state; this page just orchestrates the modal boundary
  // and the POST.
  const openCancelWizard = () => {
    resetFlashMessages();
    setCancelWizardError(null);
    setCancelWizardOpen(true);
  };

  const closeCancelWizard = () => {
    if (cancelWizardInFlight) return;
    setCancelWizardOpen(false);
    setCancelWizardError(null);
  };

  const submitCancelWizard = async ({
    reason,
    deactivateAt,
    bookingsTreatment,
  }) => {
    if (!selectedOrganizationId || !currentProgram?._id) {
      setCancelWizardError(
        "No active program is selected. Refresh the page and try again."
      );
      return;
    }
    setCancelWizardInFlight(true);
    setCancelWizardError(null);
    try {
      const payload = {
        reason,
        deactivateAt: deactivateAt || null,
        bookingsTreatment: bookingsTreatment || "auto-cancel",
      };
      await staffAPI.hardCancelOrganizationProgram(
        selectedOrganizationId,
        currentProgram._id,
        payload
      );
      setSuccess(
        `${currentProgram.name} cancelled. Issue refunds and notify HR admin manually.`
      );
      setCancelWizardOpen(false);
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      // Idempotency guard (409 already_cancelled) surfaces as a
      // user-friendly retry-friendly message. Other errors get a
      // generic fallback.
      const responseData = err?.response?.data || {};
      const code = responseData.code || responseData.error;
      let beMessage;
      if (code === "already_cancelled") {
        beMessage = "This program is already cancelled. Refresh to see current state.";
      } else if (code === "invalid_deactivate_at") {
        beMessage = "The grace-period date is invalid. Choose a future date and retry.";
      } else {
        beMessage =
          responseData.error ||
          responseData.message ||
          "Unable to cancel the program. Please try again.";
      }
      setCancelWizardError(beMessage);
    } finally {
      setCancelWizardInFlight(false);
    }
  };

  const submitLifecycleAction = async ({ reason } = {}) => {
    if (!selectedOrganizationId || !currentProgram?._id) {
      setLifecycleModalError(
        "No active program is selected. Refresh the page and try again."
      );
      return;
    }
    if (!lifecycleModalVariant) return;

    const variant = lifecycleModalVariant;
    setLifecycleModalInFlight(true);
    setLifecycleModalError(null);

    try {
      const payload = reason ? { reason } : {};
      if (variant === "suspend") {
        await staffAPI.suspendOrganizationProgram(
          selectedOrganizationId,
          currentProgram._id,
          payload
        );
        setSuccess(
          `${currentProgram.name} suspended. Reactivate at any time.`
        );
      } else if (variant === "reactivate") {
        await staffAPI.reactivateOrganizationProgram(
          selectedOrganizationId,
          currentProgram._id,
          payload
        );
        setSuccess(
          `${currentProgram.name} reactivated. Corp benefits resumed.`
        );
      }
      setLifecycleModalVariant(null);
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      // Surface a user-friendly BE error inline on the modal so the
      // operator can retry without losing context. Idempotency guard
      // hits (409 CORP_PROGRAM_ALREADY_SUSPENDED / _ALREADY_ACTIVE) get
      // rendered from the BE `error` field; unknown errors get a
      // generic fallback.
      const responseData = err?.response?.data || {};
      const beMessage =
        responseData.error ||
        responseData.message ||
        (variant === "suspend"
          ? "Unable to suspend the program. Please try again."
          : "Unable to reactivate the program. Please try again.");
      setLifecycleModalError(beMessage);
    } finally {
      setLifecycleModalInFlight(false);
    }
  };

  // CORP-FAMILY-002 — Link family member
  const openLinkFamilyMemberModal = (enrollment) => {
    resetFlashMessages();
    setLinkError(null);
    setLinkModalEnrollment(enrollment);
  };

  const closeLinkFamilyMemberModal = () => {
    if (linkInFlight) return;
    setLinkModalEnrollment(null);
    setLinkError(null);
  };

  const handleLinkFamilyMember = async ({ familyMemberUserId, reason }) => {
    if (!linkModalEnrollment?._id) return;
    const enrollmentId = linkModalEnrollment._id;
    resetFlashMessages();
    setLinkInFlight(true);
    setLinkError(null);
    setFamilyMemberRowInFlight((prev) => ({ ...prev, [enrollmentId]: true }));

    try {
      const payload = { familyMemberUserId };
      if (reason) payload.reason = reason;
      await staffAPI.linkOrganizationEnrollmentFamilyMember(
        enrollmentId,
        payload
      );
      setLinkModalEnrollment(null);
      setSuccess("Family member linked.");
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      const responseData = err?.response?.data || {};
      const code = responseData.code || responseData.error;
      if (
        code === "CORP_FAMILY_COOLDOWN_ACTIVE" ||
        code === "CORP_FAMILY_LINK_OCCUPIED" ||
        code === "CORP_FAMILY_CUSTOMER_ALREADY_LINKED"
      ) {
        setLinkError({
          code,
          message: responseData.message,
          cooldownEnd: responseData.cooldownEnd,
          priorFamilyMemberName: responseData.priorFamilyMemberName,
          existingEnrollmentEmployeeName:
            responseData.existingEnrollmentEmployeeName,
          existingFamilyMemberName: responseData.existingFamilyMemberName,
        });
      } else {
        handleApiError(
          err,
          "CorporateProgramsPage.linkFamilyMember",
          setError,
          "Unable to link the family member."
        );
        setLinkModalEnrollment(null);
      }
    } finally {
      setLinkInFlight(false);
      setFamilyMemberRowInFlight((prev) => {
        const next = { ...prev };
        delete next[enrollmentId];
        return next;
      });
    }
  };

  // CORP-FAMILY-002 — Unlink (manager-PIN)
  const openUnlinkFamilyMemberModal = (enrollment) => {
    resetFlashMessages();
    setUnlinkError(null);
    setUnlinkSuccessFlash(false);
    setUnlinkModalEnrollment(enrollment);
  };

  const closeUnlinkFamilyMemberModal = () => {
    if (unlinkInFlight || unlinkSuccessFlash) return;
    setUnlinkModalEnrollment(null);
    setUnlinkError(null);
  };

  const handleUnlinkFamilyMember = async ({ managerPin, reason }) => {
    if (!unlinkModalEnrollment?._id) return;
    const enrollmentId = unlinkModalEnrollment._id;
    resetFlashMessages();
    setUnlinkInFlight(true);
    setUnlinkError(null);
    setFamilyMemberRowInFlight((prev) => ({ ...prev, [enrollmentId]: true }));

    try {
      await staffAPI.unlinkOrganizationEnrollmentFamilyMember(enrollmentId, {
        managerPin,
        reason,
      });
      setUnlinkSuccessFlash(true);
      setSuccess("Family member unlinked.");
      // Brief success flash before close
      setTimeout(() => {
        setUnlinkSuccessFlash(false);
        setUnlinkModalEnrollment(null);
      }, 1500);
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      const responseData = err?.response?.data || {};
      const code = responseData.code || responseData.error;
      // CORP-FAMILY-005: PR #205 returns 403 for MANAGER_PIN_INVALID +
      // MANAGER_PIN_NOT_PROVISIONED. Both should surface as inline modal
      // error (NOT a global toast that closes the modal) so staff can
      // retry without losing modal state. The api.js interceptor does not
      // redirect on 403, so these arrive here intact. See
      // `isManagerPinFailure403` predicate in `services/api.js` for the
      // contract details.
      if (code === "MANAGER_PIN_INVALID") {
        setUnlinkError("Invalid manager PIN. Please try again.");
      } else if (code === "MANAGER_PIN_NOT_PROVISIONED") {
        setUnlinkError(
          "Manager PIN is not set up. Ask an admin to provision a manager PIN before unlinking family members."
        );
      } else {
        handleApiError(
          err,
          "CorporateProgramsPage.unlinkFamilyMember",
          setError,
          "Unable to unlink the family member."
        );
        setUnlinkModalEnrollment(null);
      }
    } finally {
      setUnlinkInFlight(false);
      setFamilyMemberRowInFlight((prev) => {
        const next = { ...prev };
        delete next[enrollmentId];
        return next;
      });
    }
  };

  const unlinkSubtitle = useMemo(() => {
    if (!unlinkModalEnrollment) return "";
    const familyMember = unlinkModalEnrollment.familyMember;
    const employee = unlinkModalEnrollment.user;
    const familyMemberName = familyMember
      ? `${familyMember.firstName || ""} ${familyMember.lastName || ""}`.trim() ||
        "the linked family member"
      : "the linked family member";
    const employeeName = employee
      ? `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
        "this enrollment"
      : "this enrollment";
    return `Unlinking ${familyMemberName} from ${employeeName}'s enrollment. Future bookings for ${familyMemberName} revert to standard rate. 90-day cooldown begins after unlink.`;
  }, [unlinkModalEnrollment]);

  // CORP-FAMILY-003 — Cooldown override (manager-PIN)
  const openCooldownOverrideModal = (enrollment) => {
    resetFlashMessages();
    setCooldownOverrideError(null);
    setCooldownOverrideSuccessFlash(false);
    setCooldownOverrideModalEnrollment(enrollment);
  };

  const closeCooldownOverrideModal = () => {
    if (cooldownOverrideInFlight || cooldownOverrideSuccessFlash) return;
    setCooldownOverrideModalEnrollment(null);
    setCooldownOverrideError(null);
  };

  const handleCooldownOverride = async ({ managerPin, reason }) => {
    if (!cooldownOverrideModalEnrollment?._id) return;
    const enrollmentId = cooldownOverrideModalEnrollment._id;
    resetFlashMessages();
    setCooldownOverrideInFlight(true);
    setCooldownOverrideError(null);
    setFamilyMemberRowInFlight((prev) => ({ ...prev, [enrollmentId]: true }));

    try {
      await staffAPI.overrideOrganizationEnrollmentFamilyMemberCooldown(
        enrollmentId,
        { managerPin, reason }
      );
      setCooldownOverrideSuccessFlash(true);
      setSuccess("90-day cooldown cleared.");
      // Brief success flash before close (mirrors unlink modal cadence)
      setTimeout(() => {
        setCooldownOverrideSuccessFlash(false);
        setCooldownOverrideModalEnrollment(null);
      }, 1500);
      await loadOrganizationDetail(selectedOrganizationId);
    } catch (err) {
      const responseData = err?.response?.data || {};
      const code = responseData.code || responseData.error;
      // PR #205/#206 contract: MANAGER_PIN_INVALID +
      // MANAGER_PIN_NOT_PROVISIONED + MANAGER_PIN_FORBIDDEN_ROLE arrive as
      // 403. The api.js interceptor does NOT redirect on 403 (see
      // `isManagerPinFailure403` predicate), so these arrive intact and we
      // surface inline so the modal stays open for retry.
      if (code === "MANAGER_PIN_INVALID") {
        setCooldownOverrideError("Invalid manager PIN. Please try again.");
      } else if (code === "MANAGER_PIN_NOT_PROVISIONED") {
        setCooldownOverrideError(
          "Manager PIN is not set up. Ask an admin to provision a manager PIN before overriding the cooldown."
        );
      } else if (code === "MANAGER_PIN_FORBIDDEN_ROLE") {
        setCooldownOverrideError(
          "Your role does not have permission to override the cooldown."
        );
      } else if (code === "CORP_FAMILY_COOLDOWN_OVERRIDE_REASON_REQUIRED") {
        // FE-side button-disable normally prevents this; BE guard is
        // belt-and-suspenders against a malicious client.
        setCooldownOverrideError("Reason is required to override the cooldown.");
      } else if (code === "CORP_FAMILY_NO_COOLDOWN_TO_OVERRIDE") {
        // Idempotency guard miss (race / stale row / direct API call).
        setCooldownOverrideError(
          "This enrollment is not in cooldown — nothing to override."
        );
      } else {
        handleApiError(
          err,
          "CorporateProgramsPage.overrideCooldown",
          setError,
          "Unable to override the cooldown."
        );
        setCooldownOverrideModalEnrollment(null);
      }
    } finally {
      setCooldownOverrideInFlight(false);
      setFamilyMemberRowInFlight((prev) => {
        const next = { ...prev };
        delete next[enrollmentId];
        return next;
      });
    }
  };

  const cooldownOverrideSubtitle = useMemo(() => {
    if (!cooldownOverrideModalEnrollment) return "";
    const employee = cooldownOverrideModalEnrollment.user;
    const employeeName = employee
      ? `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
        "this enrollment"
      : "this enrollment";
    return `Clears the 90-day cooldown for ${employeeName}'s enrollment so a new family member can be linked immediately. The override is logged for quarterly review.`;
  }, [cooldownOverrideModalEnrollment]);

  // CORP-FAMILY-003 — derive cooldown state for an enrollment row. Returns
  // { active: boolean, cooldownEnd: Date | null }. Cooldown is active when
  // (a) no family member currently linked AND (b) familyMemberUnlinkedAt is
  // within the past 90 days. Mirrors the BE `computeCooldownEnd` semantics
  // (ADR-037 D1) — the FE-side check is purely for affordance rendering;
  // the BE is the source of truth on every override request.
  const deriveCooldownState = (enrollment) => {
    if (!enrollment || enrollment.familyMember || enrollment.familyMemberUserId) {
      return { active: false, cooldownEnd: null };
    }
    const unlinkedAt = enrollment.familyMemberUnlinkedAt;
    if (!unlinkedAt) return { active: false, cooldownEnd: null };
    const unlinkedDate = new Date(unlinkedAt);
    if (Number.isNaN(unlinkedDate.getTime())) {
      return { active: false, cooldownEnd: null };
    }
    const cooldownEnd = new Date(unlinkedDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    return { active: cooldownEnd > new Date(), cooldownEnd };
  };

  const formatCooldownDate = (date) => {
    if (!date) return "";
    try {
      return date
        .toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "America/Phoenix",
        })
        .concat(" MST");
    } catch (_err) {
      return "";
    }
  };

  return (
    <StaffLayout>
      <div className="corporate-programs-page">
        <div className="corporate-page-header">
          <div>
            <p className="corporate-eyebrow">Corporate Programs</p>
            <h1>Employer-sponsored wellness access</h1>
            <p className="corporate-intro">
              Set up organizations like GRFD, define the monthly rule set, and
              enroll employees without touching Mongo or Stripe by hand.
            </p>
          </div>
        </div>

        {error ? <div className="corporate-alert error">{error}</div> : null}
        {success ? <div className="corporate-alert success">{success}</div> : null}

        <div className="corporate-tab-strip" role="tablist" aria-label="Corporate Programs sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "enrollments"}
            className={`corporate-tab${activeTab === "enrollments" ? " is-active" : ""}`}
            onClick={() => setActiveTab("enrollments")}
          >
            Enrollments
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "activity"}
            className={`corporate-tab${activeTab === "activity" ? " is-active" : ""}`}
            onClick={() => setActiveTab("activity")}
          >
            Activity
          </button>
        </div>

        {activeTab === "activity" ? (
          <FamilyMemberAuditLogTab
            organizationId={selectedOrganizationId}
            fetchAuditLog={({ organizationId, dateFrom, limit }) =>
              staffAPI.getOrganizationFamilyAuditLog(organizationId, {
                dateFrom,
                limit,
              })
            }
            // CORP-FAMILY-004 Section A — when staff lands on Activity tab
            // without an org selected, the empty-state CTA flips back to
            // Enrollments where the canonical org-picker lives.
            onPickOrganization={() => setActiveTab("enrollments")}
          />
        ) : (
        <>
        <div className="corporate-layout-grid">
          <section className="corporate-card organization-list-card">
            <div className="corporate-card-header">
              <div>
                <h2>Organizations</h2>
                <p>Pick an employer to manage its program and enrollments.</p>
              </div>
            </div>

            {loading ? (
              <p className="corporate-empty-state">Loading organizations...</p>
            ) : organizations.length === 0 ? (
              <p className="corporate-empty-state">
                No organizations have been created yet.
              </p>
            ) : (
              <div className="organization-list">
                {organizations.map((organization) => (
                  <button
                    key={organization._id}
                    type="button"
                    className={`organization-list-item ${
                      organization._id === selectedOrganizationId ? "selected" : ""
                    }`}
                    onClick={() => setSelectedOrganizationId(organization._id)}
                  >
                    <span className="organization-icon">
                      <FaBuilding />
                    </span>
                    <span className="organization-copy">
                      <strong>{organization.name}</strong>
                      <span>{organization.code}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="corporate-card">
            <div className="corporate-card-header">
              <div>
                <h2>Create organization</h2>
                <p>Start with the employer record before setting up the program.</p>
              </div>
            </div>

            <form className="corporate-form" onSubmit={handleOrganizationSubmit}>
              <label>
                Organization name
                <input
                  type="text"
                  value={organizationForm.name}
                  onChange={(event) =>
                    setOrganizationForm((current) => ({
                      ...current,
                      name: event.target.value,
                    }))
                  }
                  placeholder="Golder Ranch Fire District"
                />
              </label>

              <label>
                Organization code
                <input
                  type="text"
                  value={organizationForm.code}
                  onChange={(event) =>
                    setOrganizationForm((current) => ({
                      ...current,
                      code: event.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="GRFD"
                />
              </label>

              <label>
                Notes
                <textarea
                  rows="3"
                  value={organizationForm.notes}
                  onChange={(event) =>
                    setOrganizationForm((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                  placeholder="Optional internal notes for staff."
                />
              </label>

              <button type="submit" className="corporate-primary-button">
                <FaPlus />
                <span>Create organization</span>
              </button>
            </form>
          </section>
        </div>

        <div className="corporate-layout-grid detail-grid">
          <section className="corporate-card corporate-detail-card">
            <div className="corporate-card-header">
              <div>
                <h2>{selectedOrganization?.name || "Organization details"}</h2>
                <p>
                  {selectedOrganization
                    ? `Review this month's usage, active program, and enrolled employees for ${selectedOrganization.code}.`
                    : "Choose an organization to see the current program and usage."}
                </p>
              </div>
            </div>

            {!selectedOrganization ? (
              <p className="corporate-empty-state">
                Choose an organization from the list to continue.
              </p>
            ) : detailLoading ? (
              <p className="corporate-empty-state">Loading organization details...</p>
            ) : (
              <>
                <div className="usage-metrics-grid">
                  <div className="usage-metric">
                    <span className="usage-label">Sponsored used</span>
                    <strong>
                      {usageSnapshot?.summary?.sponsoredUsed || currentProgramUsage?.sponsoredUsed || 0}
                      {currentProgram
                        ? ` / ${currentProgram.monthlySponsoredCap}`
                        : ""}
                    </strong>
                  </div>
                  <div className="usage-metric">
                    <span className="usage-label">Corporate-rate bookings</span>
                    <strong>
                      {usageSnapshot?.summary?.corporateRateUsed ||
                        currentProgramUsage?.corporateRateUsed ||
                        0}
                    </strong>
                  </div>
                  <div className="usage-metric">
                    <span className="usage-label">Employee-paid total</span>
                    <strong>
                      $
                      {Number(
                        usageSnapshot?.summary?.corporateRateAmount || 0
                      ).toFixed(2)}
                    </strong>
                  </div>
                  {/* CORP-FAMILY-007 (Q5 / ADR-038 D1.b) — separate
                      family-member corp-rate metric. Read-time aggregated
                      by Simone's `organizationController.getOrganizationUsage`
                      from `SessionBooking.find({corpBookingRole:"family_member",
                      ...})` (PR #220). Renders ALONGSIDE the
                      employee-only "Corporate-rate bookings" + "Employee-paid
                      total" metrics — explicitly NOT inside them, per
                      Roderick Q5 lock. The employee metric stays
                      EMPLOYEE-ONLY (family-member bookings skip the
                      cap-write path per ADR-038 D1). */}
                  <div
                    className="usage-metric"
                    data-testid="family-member-corp-rate-metric"
                  >
                    <span className="usage-label">
                      Family-member corp-rate bookings
                    </span>
                    <strong>
                      {usageSnapshot?.summary?.familyMemberCorpRateCount || 0}
                      {" / $"}
                      {Number(
                        usageSnapshot?.summary?.familyMemberCorpRateAmount || 0
                      ).toFixed(2)}
                    </strong>
                  </div>
                  <div className="usage-metric">
                    <span className="usage-label">Sponsored per employee</span>
                    <strong>
                      {currentProgram?.sponsoredSessionsPerEmployeePerMonth ?? "—"}
                    </strong>
                  </div>
                  <div className="usage-metric">
                    <span className="usage-label">Corporate rate</span>
                    <strong>
                      {typeof currentProgram?.corporateRate === "number"
                        ? `$${currentProgram.corporateRate}`
                        : "—"}
                    </strong>
                  </div>

                  {/* CORP-METRICS-V1-EXPANSION (Sprint 18) — 5 new metric
                      tiles consuming the additive `summary` fields on
                      `GET /api/organizations/:id/usage` (Simone BE PR
                      lands in parallel). Each tile renders ONLY when its
                      backing key is present — silent degradation when
                      the BE leg hasn't deployed yet (defensive: missing
                      key → tile not rendered; no error UX, no NaN%).
                      See `docs/backlog/CORP-METRICS-V1-EXPANSION-path-y-metrics-on-existing-endpoint.md`. */}

                  {/* Unique members — cluster employees + family. Render
                      only when at least one of the two counts is present
                      on the summary (Number.isFinite excludes
                      undefined/null/NaN). */}
                  {(Number.isFinite(usageSnapshot?.summary?.uniqueEmployeesCount) ||
                    Number.isFinite(
                      usageSnapshot?.summary?.uniqueFamilyMembersCount
                    )) && (
                    <div
                      className="usage-metric"
                      data-testid="unique-members-metric"
                    >
                      <span className="usage-label">Unique members</span>
                      <strong>
                        {formatCount(
                          (usageSnapshot?.summary?.uniqueEmployeesCount || 0) +
                            (usageSnapshot?.summary?.uniqueFamilyMembersCount || 0)
                        )}
                      </strong>
                      <div className="usage-metric-sub">
                        {formatCount(
                          usageSnapshot?.summary?.uniqueEmployeesCount || 0
                        )}{" "}
                        employees ·{" "}
                        {formatCount(
                          usageSnapshot?.summary?.uniqueFamilyMembersCount || 0
                        )}{" "}
                        family
                      </div>
                    </div>
                  )}

                  {/* Adoption rate — cluster both rates. Renders when at
                      least one rate key is present. Defensive: 0 → "0%"
                      (not "NaN%"); rates are 0..1 floats from BE. */}
                  {(Number.isFinite(
                    usageSnapshot?.summary?.employeeAdoptionRate
                  ) ||
                    Number.isFinite(
                      usageSnapshot?.summary?.familyMemberAdoptionRate
                    )) && (
                    <div
                      className="usage-metric"
                      data-testid="adoption-rate-metric"
                    >
                      <span className="usage-label">Adoption rate</span>
                      <strong>
                        {formatPercent(
                          usageSnapshot?.summary?.employeeAdoptionRate
                        )}
                      </strong>
                      <div className="usage-metric-sub">
                        Employees{" "}
                        {formatPercent(
                          usageSnapshot?.summary?.employeeAdoptionRate
                        )}{" "}
                        · Family{" "}
                        {formatPercent(
                          usageSnapshot?.summary?.familyMemberAdoptionRate
                        )}
                      </div>
                    </div>
                  )}

                  {/* Avg sessions per active member. 1 decimal. */}
                  {Number.isFinite(
                    usageSnapshot?.summary?.avgSessionsPerActiveMember
                  ) && (
                    <div
                      className="usage-metric"
                      data-testid="avg-sessions-per-active-metric"
                    >
                      <span className="usage-label">
                        Avg sessions / active member
                      </span>
                      <strong>
                        {Number(
                          usageSnapshot.summary.avgSessionsPerActiveMember
                        ).toFixed(1)}
                      </strong>
                    </div>
                  )}

                  {/* Corp-rate purchased revenue. BE returns DOLLARS
                      (matches deployed `corporateRateAmount`
                      convention; PR #244 commit d6827b3 BE response —
                      `3 × $25 → 75`). FE formats with thousands
                      separator via Intl.NumberFormat. */}
                  {Number.isFinite(
                    usageSnapshot?.summary?.corpRatePurchasedRevenue
                  ) && (
                    <div
                      className="usage-metric"
                      data-testid="corp-rate-purchased-revenue-metric"
                    >
                      <span className="usage-label">
                        Corp-rate purchased revenue
                      </span>
                      <strong>
                        {formatDollars(
                          usageSnapshot.summary.corpRatePurchasedRevenue
                        )}
                      </strong>
                    </div>
                  )}

                  {/* Session-type breakdown. Object map of session-type
                      → count. Empty object → "No session data this
                      period" copy (per AC). Missing key entirely →
                      tile not rendered. */}
                  {usageSnapshot?.summary?.sessionTypeBreakdown &&
                    typeof usageSnapshot.summary.sessionTypeBreakdown ===
                      "object" && (
                      <div
                        className="usage-metric"
                        data-testid="session-type-breakdown-metric"
                      >
                        <span className="usage-label">
                          Session-type breakdown
                        </span>
                        {Object.keys(
                          usageSnapshot.summary.sessionTypeBreakdown
                        ).length === 0 ? (
                          <div className="usage-metric-empty">
                            No session data this period
                          </div>
                        ) : (
                          <ul className="usage-metric-breakdown-list">
                            {Object.entries(
                              usageSnapshot.summary.sessionTypeBreakdown
                            ).map(([sessionType, count]) => (
                              <li key={sessionType}>
                                <span>{formatSessionTypeLabel(sessionType)}</span>
                                <strong>{formatCount(count || 0)}</strong>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                </div>

                <div className="current-program-summary">
                  <div className="current-program-summary-header">
                    <h3>Active program</h3>
                    {currentProgram ? (
                      <ProgramLifecycleActionsMenu
                        lifecycleState={currentProgram.lifecycleState}
                        onSuspend={() => openLifecycleModal("suspend")}
                        onReactivate={() => openLifecycleModal("reactivate")}
                        onCancel={openCancelWizard}
                        disabled={lifecycleModalInFlight || cancelWizardInFlight}
                      />
                    ) : null}
                  </div>
                  {currentProgram ? (
                    <div className="program-summary-grid">
                      <div className="program-summary-item">
                        <span className="summary-label">Program name</span>
                        <strong className="summary-value">
                          {currentProgram.name}
                          <ProgramLifecycleBadge
                            lifecycleState={currentProgram.lifecycleState}
                          />
                        </strong>
                      </div>
                      <div className="program-summary-item">
                        <span className="summary-label">Stripe price ID</span>
                        <strong className="summary-value summary-value-code">
                          {currentProgram.corporateRateStripePriceId || "Not set"}
                        </strong>
                      </div>
                      <div className="program-summary-item">
                        <span className="summary-label">Eligible services</span>
                        <div className="service-chip-list">
                          {(currentProgram.eligibleSessionTypes || []).map((type) => (
                            <span key={type} className="service-chip">
                              {type === "infrared" ? "Infrared sauna" : "Red light"}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="corporate-empty-state">
                      No active program yet. Create one below.
                    </p>
                  )}
                </div>

                <div className="employee-usage-table">
                  <div className="employee-table-header">
                    <h3>Enrolled employees this month</h3>
                    <div className="employee-table-actions">
                      <span>{usageSnapshot?.yearMonth || "Current month"}</span>
                      <button
                        type="button"
                        className="corporate-secondary-button"
                        onClick={exportMonthlyReport}
                      >
                        Export CSV
                      </button>
                    </div>
                  </div>
                  {enrollments.length === 0 ? (
                    <p className="corporate-empty-state">
                      No employees enrolled yet.
                    </p>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Email</th>
                          <th>Family Member</th>
                          <th>Sponsored used</th>
                          <th>Corporate-rate used</th>
                          <th>Employee-paid amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeeUsageRows.map(
                          ({
                            enrollment,
                            employeeUsage,
                            corporateRateAmount,
                          }) => {
                            const familyMember = enrollment.familyMember;
                            const rowInFlight = !!familyMemberRowInFlight[enrollment._id];
                            // CORP-FAMILY-003 — cooldown state takes
                            // precedence over the "+ Add Family Member"
                            // button when the row is in cooldown. Surfaces
                            // cooldown end-date UP-FRONT + manager-gated
                            // override affordance (Zuri spec Flow B1).
                            const cooldownState = !familyMember
                              ? deriveCooldownState(enrollment)
                              : { active: false, cooldownEnd: null };
                            return (
                              <tr key={enrollment._id}>
                              <td>
                                {enrollment.user ? (
                                  <CustomerNameLink
                                    customerId={enrollment.user._id}
                                  >
                                    {enrollment.user.firstName}{" "}
                                    {enrollment.user.lastName}
                                  </CustomerNameLink>
                                ) : (
                                  "Unknown member"
                                )}
                              </td>
                              <td>{enrollment.user?.email || "—"}</td>
                              <td className="family-member-cell">
                                {familyMember ? (
                                  <div className="family-member-linked">
                                    <div className="family-member-text">
                                      <strong>
                                        {familyMember.firstName}{" "}
                                        {familyMember.lastName}
                                      </strong>
                                      <span>{familyMember.email || "—"}</span>
                                    </div>
                                    <button
                                      type="button"
                                      className="family-member-unlink-button"
                                      onClick={() =>
                                        openUnlinkFamilyMemberModal(enrollment)
                                      }
                                      disabled={rowInFlight}
                                      aria-label={`Unlink family member ${familyMember.firstName} ${familyMember.lastName} — manager approval required`}
                                    >
                                      {rowInFlight ? "..." : "Unlink"}
                                    </button>
                                  </div>
                                ) : cooldownState.active ? (
                                  <div className="family-member-cooldown">
                                    <div className="family-member-cooldown-text">
                                      <strong className="family-member-cooldown-title">
                                        Cooldown active
                                      </strong>
                                      <span className="family-member-cooldown-subtitle">
                                        until {formatCooldownDate(cooldownState.cooldownEnd)}
                                      </span>
                                    </div>
                                    <button
                                      type="button"
                                      className="family-member-cooldown-override-button"
                                      onClick={() =>
                                        openCooldownOverrideModal(enrollment)
                                      }
                                      disabled={rowInFlight}
                                      aria-label={`Override 90-day cooldown for ${enrollment.user?.firstName || ""} ${enrollment.user?.lastName || ""}'s enrollment — manager approval required`}
                                    >
                                      {rowInFlight ? (
                                        "..."
                                      ) : (
                                        <>
                                          <FaLock aria-hidden="true" />
                                          <span>Override 90-day cooldown</span>
                                        </>
                                      )}
                                    </button>
                                    <span className="family-member-cooldown-helper">
                                      Manager approval required
                                    </span>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="family-member-add-button"
                                    onClick={() =>
                                      openLinkFamilyMemberModal(enrollment)
                                    }
                                    disabled={rowInFlight}
                                  >
                                    {rowInFlight ? (
                                      "..."
                                    ) : (
                                      <>
                                        <FaPlus aria-hidden="true" />
                                        <span>Add Family Member</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </td>
                              <td>{employeeUsage?.sponsoredUsed || 0}</td>
                              <td>{employeeUsage?.corporateRateUsed || 0}</td>
                              <td>${corporateRateAmount.toFixed(2)}</td>
                            </tr>
                            );
                          }
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="employee-usage-table corporate-bookings-table">
                  <div className="employee-table-header">
                    <h3>Recent corporate bookings</h3>
                    <span>{usageSnapshot?.corporateBookings?.length || 0} bookings</span>
                  </div>
                  {usageSnapshot?.corporateBookings?.length ? (
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Employee</th>
                          <th>Session</th>
                          <th>Funding</th>
                          <th>Amount</th>
                          <th>Payment</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageSnapshot.corporateBookings.map((booking) => (
                          <tr key={booking._id}>
                            <td>
                              {new Date(booking.appointmentDate).toLocaleString([], {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                              })}
                            </td>
                            <td>
                              {booking.user ? (
                                <CustomerNameLink
                                  customerId={booking.user._id}
                                >
                                  {booking.user.firstName}{" "}
                                  {booking.user.lastName}
                                </CustomerNameLink>
                              ) : (
                                "Unknown member"
                              )}
                            </td>
                            <td>
                              {booking.sessionType === "infrared"
                                ? "Infrared sauna"
                                : "Red light"}
                            </td>
                            <td>
                              <span
                                className={`corporate-booking-badge ${
                                  booking.sessionSourceType === "organization-sponsored"
                                    ? "sponsored"
                                    : "paid"
                                }`}
                              >
                                {booking.sessionSourceType === "organization-sponsored"
                                  ? "Sponsored"
                                  : "Corporate Rate"}
                              </span>
                            </td>
                            <td>
                              {booking.sessionSourceType ===
                              "organization-corporate-rate"
                                ? `$${Number(
                                    booking.organizationRateApplied || 0
                                  ).toFixed(2)}`
                                : "$0.00"}
                            </td>
                            <td>
                              {booking.sessionSourceType ===
                              "organization-corporate-rate"
                                ? booking.paymentId?.status || "pending"
                                : "not required"}
                            </td>
                            <td>{booking.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="corporate-empty-state">
                      No corporate-funded bookings found for this month yet.
                    </p>
                  )}
                </div>
              </>
            )}
          </section>

          <div className="corporate-side-stack">
            <section className="corporate-card">
              <div className="corporate-card-header">
                <div>
                  <h2>Create program</h2>
                  <p>
                    This sets the monthly sponsored rule and the employee-paid
                    corporate rate.
                  </p>
                </div>
              </div>

              <form className="corporate-form" onSubmit={handleProgramSubmit}>
                <label>
                  Program name
                  <input
                    type="text"
                    value={programForm.name}
                    onChange={(event) =>
                      setProgramForm((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="GRFD Monthly Wellness"
                  />
                </label>

                <div className="corporate-two-column">
                  <label>
                    Sponsored sessions per employee
                    <input
                      type="number"
                      min="0"
                      value={programForm.sponsoredSessionsPerEmployeePerMonth}
                      onChange={(event) =>
                        setProgramForm((current) => ({
                          ...current,
                          sponsoredSessionsPerEmployeePerMonth: Number(
                            event.target.value
                          ),
                        }))
                      }
                    />
                  </label>

                  <label>
                    Monthly sponsored cap
                    <input
                      type="number"
                      min="0"
                      value={programForm.monthlySponsoredCap}
                      onChange={(event) =>
                        setProgramForm((current) => ({
                          ...current,
                          monthlySponsoredCap: Number(event.target.value),
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="corporate-two-column">
                  <label>
                    Corporate rate
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={programForm.corporateRate}
                      onChange={(event) =>
                        setProgramForm((current) => ({
                          ...current,
                          corporateRate: Number(event.target.value),
                        }))
                      }
                    />
                  </label>

                  <label>
                    Stripe price ID
                    <input
                      type="text"
                      value={programForm.corporateRateStripePriceId}
                      onChange={(event) =>
                        setProgramForm((current) => ({
                          ...current,
                          corporateRateStripePriceId: event.target.value,
                        }))
                      }
                      placeholder="price_..."
                    />
                  </label>
                </div>

                <fieldset className="corporate-fieldset">
                  <legend>Eligible services</legend>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={programForm.eligibleSessionTypes.includes("infrared")}
                      onChange={(event) => {
                        setProgramForm((current) => ({
                          ...current,
                          eligibleSessionTypes: event.target.checked
                            ? [...current.eligibleSessionTypes, "infrared"]
                            : current.eligibleSessionTypes.filter(
                                (type) => type !== "infrared"
                              ),
                        }));
                      }}
                    />
                    <span>Infrared sauna</span>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={programForm.eligibleSessionTypes.includes("redlight")}
                      onChange={(event) => {
                        setProgramForm((current) => ({
                          ...current,
                          eligibleSessionTypes: event.target.checked
                            ? [...current.eligibleSessionTypes, "redlight"]
                            : current.eligibleSessionTypes.filter(
                                (type) => type !== "redlight"
                              ),
                        }));
                      }}
                    />
                    <span>Red light</span>
                  </label>
                </fieldset>

                <button
                  type="submit"
                  className="corporate-primary-button"
                  disabled={!selectedOrganizationId}
                >
                  <FaPlus />
                  <span>Create program</span>
                </button>
              </form>
            </section>

            <section className="corporate-card">
              <div className="corporate-card-header">
                <div>
                  <h2>Email domain auto-enroll</h2>
                  <p>
                    Auto-enroll signups whose email domain matches a corporate
                    program, like <strong>grfdaz.gov</strong>.
                  </p>
                </div>
              </div>

              <form className="corporate-form" onSubmit={handleDomainRuleSubmit}>
                <label>
                  Program
                  <select
                    value={domainRuleForm.programId}
                    onChange={(event) =>
                      setDomainRuleForm((current) => ({
                        ...current,
                        programId: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select a program</option>
                    {(usageSnapshot?.programs || []).map((program) => (
                      <option key={program._id} value={program._id}>
                        {program.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Email domain
                  <input
                    type="text"
                    value={domainRuleForm.domain}
                    onChange={(event) =>
                      setDomainRuleForm((current) => ({
                        ...current,
                        domain: event.target.value.toLowerCase().trim(),
                      }))
                    }
                    placeholder="grfdaz.gov"
                  />
                </label>

                <fieldset className="corporate-fieldset">
                  <legend>Rule settings</legend>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={domainRuleForm.autoEnroll}
                      onChange={(event) =>
                        setDomainRuleForm((current) => ({
                          ...current,
                          autoEnroll: event.target.checked,
                        }))
                      }
                    />
                    <span>Auto-enroll matching signups</span>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={domainRuleForm.requireEmailVerification}
                      onChange={(event) =>
                        setDomainRuleForm((current) => ({
                          ...current,
                          requireEmailVerification: event.target.checked,
                        }))
                      }
                    />
                    <span>Require email verification first</span>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={domainRuleForm.active}
                      onChange={(event) =>
                        setDomainRuleForm((current) => ({
                          ...current,
                          active: event.target.checked,
                        }))
                      }
                    />
                    <span>Rule is active</span>
                  </label>
                </fieldset>

                <label>
                  Notes
                  <textarea
                    rows="2"
                    value={domainRuleForm.notes}
                    onChange={(event) =>
                      setDomainRuleForm((current) => ({
                        ...current,
                        notes: event.target.value,
                      }))
                    }
                    placeholder="Optional staff note for this domain."
                  />
                </label>

                <button
                  type="submit"
                  className="corporate-primary-button"
                  disabled={!selectedOrganizationId}
                >
                  <FaPlus />
                  <span>Create domain rule</span>
                </button>
              </form>

              <div className="domain-rule-list">
                {domainRules.length === 0 ? (
                  <p className="corporate-empty-state">
                    No domain rules configured yet.
                  </p>
                ) : (
                  domainRules.map((rule) => (
                    <div className="domain-rule-item" key={rule._id}>
                      <div className="domain-rule-copy">
                        <strong>{rule.domain}</strong>
                        <span>{rule.program?.name || "Unknown program"}</span>
                      </div>
                      <div className="domain-rule-actions">
                        <button
                          type="button"
                          className={`domain-toggle-button ${
                            rule.autoEnroll ? "is-on" : ""
                          }`}
                          onClick={() =>
                            handleDomainRuleUpdate(rule._id, {
                              autoEnroll: !rule.autoEnroll,
                            })
                          }
                        >
                          Auto-enroll: {rule.autoEnroll ? "On" : "Off"}
                        </button>
                        <button
                          type="button"
                          className={`domain-toggle-button ${
                            rule.requireEmailVerification ? "is-on" : ""
                          }`}
                          onClick={() =>
                            handleDomainRuleUpdate(rule._id, {
                              requireEmailVerification:
                                !rule.requireEmailVerification,
                            })
                          }
                        >
                          Verify first:{" "}
                          {rule.requireEmailVerification ? "Yes" : "No"}
                        </button>
                        <button
                          type="button"
                          className={`domain-toggle-button ${
                            rule.active ? "is-on" : ""
                          }`}
                          onClick={() =>
                            handleDomainRuleUpdate(rule._id, {
                              active: !rule.active,
                            })
                          }
                        >
                          {rule.active ? "Active" : "Inactive"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="corporate-card">
              <div className="corporate-card-header">
                <div>
                  <h2>Enroll employee</h2>
                  <p>
                    Search an existing member account, then assign the active
                    organization program.
                  </p>
                </div>
              </div>

              <form className="corporate-form" onSubmit={handleEnrollmentSubmit}>
                <label>
                  Search members
                  <div className="employee-search-row">
                    <input
                      type="text"
                      value={employeeSearch}
                      onChange={(event) => setEmployeeSearch(event.target.value)}
                      placeholder="Name, email, or phone"
                    />
                    <button
                      type="button"
                      className="corporate-secondary-button"
                      onClick={handleEmployeeSearch}
                    >
                      <FaSearch />
                      <span>{employeeSearchLoading ? "Searching" : "Search"}</span>
                    </button>
                  </div>
                </label>

                {employeeResults.length > 0 ? (
                  <div className="employee-results-list">
                    {employeeResults.map((customer) => {
                      const fullName = `${customer.firstName} ${customer.lastName}`;
                      const isSelected = enrollmentForm.userId === customer._id;

                      return (
                        <button
                          type="button"
                          key={customer._id}
                          className={`employee-result-item ${
                            isSelected ? "selected" : ""
                          }`}
                          onClick={() =>
                            setEnrollmentForm((current) => ({
                              ...current,
                              userId: customer._id,
                            }))
                          }
                        >
                          <span className="employee-result-name">{fullName}</span>
                          <span className="employee-result-meta">
                            {customer.email}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : employeeSearch && !employeeSearchLoading ? (
                  <p className="corporate-empty-state">
                    No matching members found yet.
                  </p>
                ) : null}

                <label>
                  Employee identifier
                  <input
                    type="text"
                    value={enrollmentForm.employeeIdentifier}
                    onChange={(event) =>
                      setEnrollmentForm((current) => ({
                        ...current,
                        employeeIdentifier: event.target.value,
                      }))
                    }
                    placeholder="Badge number or internal ID"
                  />
                </label>

                <button
                  type="submit"
                  className="corporate-primary-button"
                  disabled={!selectedOrganizationId || !currentProgram}
                >
                  <FaUsers />
                  <span>Save enrollment</span>
                </button>
              </form>
            </section>
          </div>
        </div>
        </>
        )}
      </div>

      <LinkFamilyMemberModal
        isOpen={!!linkModalEnrollment}
        enrollment={linkModalEnrollment}
        onClose={closeLinkFamilyMemberModal}
        onConfirm={handleLinkFamilyMember}
        inFlight={linkInFlight}
        error={linkError}
        onErrorCleared={() => setLinkError(null)}
        searchCustomers={(params) => staffAPI.searchCustomers(params)}
      />

      <ManagerPinModal
        isOpen={!!unlinkModalEnrollment}
        title="Manager approval — Unlink family member"
        subtitle={unlinkSubtitle}
        reasonPlaceholder="e.g. household change / employee request / spouse separation"
        reasonRequired
        pinLength={6}
        inFlight={unlinkInFlight}
        error={unlinkError}
        successFlash={unlinkSuccessFlash}
        onCancel={closeUnlinkFamilyMemberModal}
        onConfirm={handleUnlinkFamilyMember}
        onErrorCleared={() => setUnlinkError(null)}
      />

      <ManagerPinModal
        isOpen={!!cooldownOverrideModalEnrollment}
        title="Manager approval — Override cooldown"
        subtitle={cooldownOverrideSubtitle}
        reasonPlaceholder="e.g. divorce finalized; new spouse linked per HR contact"
        reasonRequired
        pinLength={6}
        inFlight={cooldownOverrideInFlight}
        error={cooldownOverrideError}
        successFlash={cooldownOverrideSuccessFlash}
        onCancel={closeCooldownOverrideModal}
        onConfirm={handleCooldownOverride}
        onErrorCleared={() => setCooldownOverrideError(null)}
      />

      <ProgramLifecycleActionModal
        isOpen={!!lifecycleModalVariant}
        variant={lifecycleModalVariant || "suspend"}
        programName={currentProgram?.name}
        inFlight={lifecycleModalInFlight}
        error={lifecycleModalError}
        onClose={closeLifecycleModal}
        onConfirm={submitLifecycleAction}
      />

      <ProgramLifecycleCancelWizard
        isOpen={cancelWizardOpen}
        programName={currentProgram?.name}
        inFlight={cancelWizardInFlight}
        error={cancelWizardError}
        onClose={closeCancelWizard}
        onConfirm={submitCancelWizard}
      />
    </StaffLayout>
  );
};

export default CorporateProgramsPage;
